import styles from './GlobalCopilotOverlay.module.css'
import {ActionList, Button, IconButton, RelativeTime} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {
  CheckCircleFillIcon,
  CommentIcon,
  GearIcon,
  GitMergeIcon,
  GitMergeQueueIcon,
  GitPullRequestClosedIcon,
  DotFillIcon,
  SkipIcon,
  StopIcon,
} from '@primer/octicons-react'
import {CheckSpinner} from '@github-ui/mergebox'
import {Icon} from '@github-ui/pacer/Icon'
import {useMemo} from 'react'

// TODO: Replace with real data
const now = new Date()
const tasks = [
  {
    id: 'fix-api-endpoints',
    title: 'Fix critical API endpoints and implement comprehensive error handling with retry logic',
    repo: 'github/api-service',
    startDate: new Date(now.getTime() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    state: 'completed',
  },
  {
    id: 'update-dashboard',
    title: 'Update user dashboard with new analytics widgets and performance optimizations for better UX',
    repo: 'acme/frontend',
    startDate: new Date(now.getTime() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
    state: 'in_progress',
  },
  {
    id: 'refactor-queries',
    title: 'Refactor database queries',
    repo: 'tech/backend',
    startDate: new Date(now.getTime() - 22 * 60 * 60 * 1000).toISOString(), // 22 hours ago
    state: 'failed',
  },
  {
    id: 'add-tests',
    title: 'Add unit tests',
    repo: 'dev/testing',
    startDate: new Date(now.getTime() - 105 * 60 * 1000).toISOString(), // 1 hour 45 minutes ago
    state: 'merged',
  },
  {
    id: 'security-patch',
    title: 'Deploy security patch',
    repo: 'ops/infrastructure',
    startDate: new Date(now.getTime() - 10 * 60 * 1000).toISOString(), // 10 minutes ago
    state: 'inMergeQueue',
  },
].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())

export function GlobalCopilotOverlay() {
  return (
    <div className={styles.overlay}>
      <OverlayHeader />
      <OverlayContent />
      {tasks.length > 0 && <OverlayFooter />}
    </div>
  )
}

function OverlayHeader() {
  return (
    <div className={styles.header}>
      <h2 className="f5">Copilot</h2>
      <div className={styles.headerActions}>
        {/* TODO: Make this work */}
        <Button variant="invisible" leadingVisual={CommentIcon}>
          Open assistive
        </Button>
        <IconButton variant="invisible" aria-label="Your Copilot settings" icon={GearIcon} />
      </div>
    </div>
  )
}

export function OverlayContent() {
  return (
    // TODO: Add loading and failure states
    // TODO: Add conditional shadows on top and bottom when content is hidden by overflow in either direction.
    <div className={styles.content}>
      {tasks.length === 0 ? (
        <Blankslate>
          <Blankslate.Heading>Let Copilot tackle your TODOs</Blankslate.Heading>
          <Blankslate.Description className="text-center text-wrap-balance">
            Delegate tasks to GitHub Copilot coding agent to work on in the background, and then monitor Copilot’s
            progress.
          </Blankslate.Description>
          <Blankslate.PrimaryAction href="/copilot/agents">Start a new task</Blankslate.PrimaryAction>
        </Blankslate>
      ) : (
        <AgentTasks />
      )}
    </div>
  )
}

export function OverlayFooter() {
  return (
    <div className={styles.footer}>
      <Button variant="invisible" block>
        View all agents
      </Button>
    </div>
  )
}

function AgentTasks() {
  // This is copied from AgentsViewV2.tsx and modified to work in this proof of concept.
  // We should probably DRY this up though.
  const getLeadingVisualIconAttributes = useMemo(() => {
    return (task: (typeof tasks)[0]) => {
      if (task.state === 'inMergeQueue') {
        return {
          icon: GitMergeQueueIcon,
          color: 'var(--fgColor-attention)' as const,
          'data-testid': 'icon-git-merge-queue',
        }
      }

      if (task.state === 'completed') {
        return {
          icon: CheckCircleFillIcon,
          color: 'var(--fgColor-success)' as const,
          'data-testid': 'icon-check-circle-fill',
        }
      }

      if (task.state === 'failed' || task.state === 'timed_out') {
        return {
          icon: StopIcon,
          color: 'var(--fgColor-danger)' as const,
          'data-testid': 'icon-stop',
        }
      }

      if (task.state === 'in_progress') {
        return {
          icon: () => <CheckSpinner animated />,
          color: 'attention.fg' as const,
          'data-testid': 'icon-spinning-circle',
        }
      }

      if (task.state === 'idle') {
        return {
          icon: DotFillIcon,
          color: 'var(--fgColor-muted)' as const,
          'data-testid': 'icon-dot',
        }
      }

      if (task.state === 'cancelled') {
        return {
          icon: SkipIcon,
          color: 'var(--fgColor-muted)' as const,
          'data-testid': 'icon-skip',
        }
      }

      if (task.state === 'merged') {
        return {
          icon: GitMergeIcon,
          color: 'var(--fgColor-done)' as const,
          'data-testid': 'icon-git-merge',
        }
      }

      if (task.state === 'closed') {
        return {
          icon: GitPullRequestClosedIcon,
          color: 'var(--fgColor-danger)' as const,
          'data-testid': 'icon-skip',
        }
      }

      return {
        icon: DotFillIcon,
        color: 'var(--fgColor-muted)' as const,
        'data-testid': 'icon-idle',
      }
    }
  }, [])

  return (
    <ActionList showDividers>
      <ActionList.Group>
        <ActionList.GroupHeading as="h2">Recent tasks</ActionList.GroupHeading>
        {tasks.map(task => {
          const iconAttributes = getLeadingVisualIconAttributes(task)
          return (
            <ActionList.Item key={task.id} className={styles.task}>
              <ActionList.LeadingVisual>
                <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
              </ActionList.LeadingVisual>
              {task.title}
              <ActionList.Description className="no-wrap mr-2">{task.repo}</ActionList.Description>
              <ActionList.TrailingVisual>
                <RelativeTime datetime={task.startDate} format="micro" className={styles.taskStartDate} />
              </ActionList.TrailingVisual>
            </ActionList.Item>
          )
        })}
      </ActionList.Group>
    </ActionList>
  )
}

try{ GlobalCopilotOverlay.displayName ||= 'GlobalCopilotOverlay' } catch {}
try{ OverlayHeader.displayName ||= 'OverlayHeader' } catch {}
try{ OverlayContent.displayName ||= 'OverlayContent' } catch {}
try{ OverlayFooter.displayName ||= 'OverlayFooter' } catch {}
try{ AgentTasks.displayName ||= 'AgentTasks' } catch {}